import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, resolveImg, formatError } from "@/lib/api";
import ImageCropDialog from "@/components/ImageCropDialog";
import { Pencil, Trash2, Plus, X, Star, ChevronLeft, ChevronRight, ExternalLink, List, LayoutGrid, Crop, Eye } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

const STATUSES = ["Available", "Archive"];
const STYLES = ["Classic", "Modern"];

const emptyForm = {
    name: "", description: "", category_ids: [], room: "", materials: [],
    dimensions: "", care_instructions: "", status: "Available", style: "",
    price: "", images: [], card_image: "", featured: false, sale_price: "", sale_percentage: "",
    slug: "", visible: true, currency: "EUR",
};

export default function AdminProducts() {
    const t = useT();
    const [products, setProducts] = useState([]);
    const [total, setTotal] = useState(0);
    const [categories, setCategories] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [materialInput, setMaterialInput] = useState("");
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [styleFilter, setStyleFilter] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [cropSrc, setCropSrc] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [view, setView] = useState(() => localStorage.getItem("admin-products-view") || "list");
    const setViewMode = (v) => { setView(v); localStorage.setItem("admin-products-view", v); };
    const confirmTimer = useRef(null);
    useEffect(() => () => clearTimeout(confirmTimer.current), []);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }, []);

    const loadProducts = async (currentPage, currentPageSize) => {
        const ps = currentPageSize ?? pageSize;
        const params = { page: currentPage, page_size: ps };
        if (search.trim()) params.q = search.trim();
        if (statusFilter) params.status = statusFilter;
        if (categoryFilter) params.category = categoryFilter;
        if (styleFilter) params.style = styleFilter;
        try {
            const res = await api.get("/products", { params });
            setProducts(res.data?.items ?? []);
            setTotal(res.data?.total ?? 0);
        } catch {
            toast.error("Failed to load products");
        }
    };

    useEffect(() => { loadProducts(page, pageSize); }, [page, pageSize]);

    useEffect(() => {
        if (page === 1) { loadProducts(1, pageSize); } else { setPage(1); }
    }, [search, statusFilter, categoryFilter, styleFilter]);

    function slugify(s) {
        return s.toLowerCase().trim()
            .replace(/[^a-z0-9\s-]/g, "")
            .replace(/\s+/g, "-")
            .replace(/-+/g, "-");
    }

    const openNew = () => {
        setEditing(null);
        setForm({ ...emptyForm, category_ids: categories[0]?.id ? [categories[0].id] : [] });
        setMaterialInput("");
        setOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({
            name: p.name, description: p.description || "",
            category_ids: p.category_ids?.length ? p.category_ids : (p.category_id ? [p.category_id] : []),
            room: p.room || "", materials: p.materials || [], dimensions: p.dimensions || "",
            care_instructions: p.care_instructions || "", status: p.status || "Available",
            style: p.style || "", price: p.price || "", images: p.images || [],
            card_image: p.card_image || "",
            featured: !!p.featured, sale_price: p.sale_price || "",
            sale_percentage: p.sale_percentage || "", slug: p.slug || "", visible: p.visible ?? true,
            currency: p.currency || "EUR",
        });
        setMaterialInput("");
        setOpen(true);
    };

    const addMaterial = () => {
        const v = materialInput.trim();
        if (v && !form.materials.includes(v)) setForm({ ...form, materials: [...form.materials, v] });
        setMaterialInput("");
    };

    const removeMaterial = (m) => setForm({ ...form, materials: form.materials.filter((x) => x !== m) });

    const handleUpload = async (files) => {
        if (!files?.length) return;
        setUploading(true);
        try {
            const uploaded = [];
            for (const file of Array.from(files)) {
                const fd = new FormData();
                fd.append("file", file);
                const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(data.url);
            }
            setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
            toast.success(`${uploaded.length} image(s) uploaded`);
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    // Load the stored photo as a local blob first: a blob: URL is same-origin, so the
    // crop canvas never gets tainted by the cross-origin image host.
    const openCardCrop = async (img) => {
        const url = resolveImg(img);
        try {
            // cache: "no-store" is required — the <img> above already cached this URL as a
            // no-cors response, and reusing that entry makes the CORS check fail.
            const res = await fetch(url, { mode: "cors", cache: "no-store" });
            if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
            setCropSrc(URL.createObjectURL(await res.blob()));
        } catch (err) {
            // Fall back to cropping straight from the URL; works whenever the canvas
            // does not end up tainted.
            console.error("Card crop: blob fetch failed, using direct URL", err);
            setCropSrc(url);
        }
    };

    const closeCardCrop = () => {
        setCropSrc((url) => { if (url) URL.revokeObjectURL(url); return null; });
    };

    // The crop never touches `images` — it produces a separate thumbnail used only in listings.
    const uploadCardCrop = async (blob) => {
        if (!blob) { closeCardCrop(); return; }
        const ext = blob.type === "image/png" ? "png" : "jpg";
        const file = new File([blob], `card-${Date.now()}.${ext}`, { type: blob.type || "image/jpeg" });
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({ ...f, card_image: data.url }));
            toast.success(t("admin.prod.field.card.saved"));
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
            closeCardCrop();
        }
    };

    const removeImage = (idx) => setForm({ ...form, images: form.images.filter((_, i) => i !== idx) });

    const toggleCategory = (id) => setForm((f) => ({
        ...f,
        category_ids: f.category_ids.includes(id)
            ? f.category_ids.filter((x) => x !== id)
            : [...f.category_ids, id],
    }));

    const save = async () => {
        if (!form.name || !form.category_ids.length) {
            toast.error("Name and at least one collection are required.");
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/products/${editing.id}`, form);
                toast.success("Piece updated");
            } else {
                await api.post("/products", form);
                toast.success("Piece added to catalog");
            }
            setOpen(false);
            if (!editing && page !== 1) {
                setPage(1); // jump to first page so the new piece is visible at the top
            } else {
                loadProducts(page, pageSize);
            }
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async (p) => {
        // First click arms the delete; a second click within 3s confirms it.
        if (confirmId !== p.id) {
            setConfirmId(p.id);
            clearTimeout(confirmTimer.current);
            confirmTimer.current = setTimeout(() => setConfirmId(null), 3000);
            return;
        }
        clearTimeout(confirmTimer.current);
        setConfirmId(null);
        try {
            await api.delete(`/products/${p.id}`);
            toast.success(t("admin.trash.moved"));
            loadProducts(page, pageSize);
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Delete failed");
        }
    };

    return (
        <div className="p-8 md:p-12" data-testid="admin-products">
            <header className="flex items-center justify-between flex-wrap gap-4 mb-10">
                <div>
                    <p className="overline">{t("admin.prod.overline")}</p>
                    <h1 className="font-serif text-4xl md:text-5xl mt-2">{t("admin.prod.title")}</h1>
                </div>
                <button
                    onClick={openNew}
                    data-testid="new-product-btn"
                    className="inline-flex items-center gap-2 bg-melilux-gold text-melilux-bg px-6 py-3 text-[0.7rem] uppercase tracking-[0.28em] hover:bg-melilux-goldBright transition-colors"
                >
                    <Plus size={14} /> {t("admin.prod.new")}
                </button>
            </header>

            <div className="flex flex-wrap gap-3 mb-6">
                <input
                    type="text"
                    placeholder={t("admin.prod.search")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="product-search"
                    className="bg-melilux-bg border border-white/15 px-4 py-3 text-sm text-melilux-cream focus:border-melilux-gold outline-none min-w-[280px]"
                />
                <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    data-testid="product-category-filter"
                    className="bg-melilux-bg border border-white/15 px-4 py-3 text-sm text-melilux-cream focus:border-melilux-gold outline-none"
                >
                    <option value="">{t("admin.prod.filter.collections")}</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select
                    value={styleFilter}
                    onChange={(e) => setStyleFilter(e.target.value)}
                    data-testid="product-style-filter"
                    className="bg-melilux-bg border border-white/15 px-4 py-3 text-sm text-melilux-cream focus:border-melilux-gold outline-none"
                >
                    <option value="">{t("admin.prod.filter.styles")}</option>
                    {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    data-testid="product-status-filter"
                    className="bg-melilux-bg border border-white/15 px-4 py-3 text-sm text-melilux-cream focus:border-melilux-gold outline-none"
                >
                    <option value="">{t("admin.prod.filter.statuses")}</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {(categoryFilter || styleFilter || statusFilter || search) && (
                    <button
                        onClick={() => { setCategoryFilter(""); setStyleFilter(""); setStatusFilter(""); setSearch(""); }}
                        data-testid="product-clear-filters"
                        className="text-[0.7rem] uppercase tracking-[0.25em] text-melilux-gold link-underline px-2"
                    >
                        {t("admin.prod.filter.clear")}
                    </button>
                )}
                <div className="ml-auto flex items-center border border-white/15" data-testid="products-view-toggle">
                    <button
                        onClick={() => setViewMode("list")}
                        title={t("admin.view.list")}
                        data-testid="products-view-list"
                        className={`p-3 transition-colors ${view === "list" ? "text-melilux-gold bg-white/[0.05]" : "text-melilux-cream/50 hover:text-melilux-cream"}`}
                    >
                        <List size={16} strokeWidth={1.5} />
                    </button>
                    <button
                        onClick={() => setViewMode("grid")}
                        title={t("admin.view.grid")}
                        data-testid="products-view-grid"
                        className={`p-3 transition-colors ${view === "grid" ? "text-melilux-gold bg-white/[0.05]" : "text-melilux-cream/50 hover:text-melilux-cream"}`}
                    >
                        <LayoutGrid size={16} strokeWidth={1.5} />
                    </button>
                </div>
            </div>

            {view === "list" && (
            <div className="border border-white/10 bg-melilux-bg overflow-x-auto" data-testid="products-table">
                <table className="w-full text-sm min-w-[840px]">
                    <thead>
                    <tr className="text-left text-[0.65rem] uppercase tracking-[0.25em] text-melilux-cream/45 border-b border-white/10">
                        <th className="py-4 px-5">{t("admin.prod.col.piece")}</th>
                        <th className="py-4 px-5">{t("admin.prod.col.collection")}</th>
                        <th className="py-4 px-5">{t("admin.prod.col.style")}</th>
                        <th className="py-4 px-5">{t("admin.prod.col.price")}</th>
                        <th className="py-4 px-5">{t("admin.prod.col.status")}</th>
                        <th className="py-4 px-5">{t("admin.prod.col.featured")}</th>
                        <th className="py-4 px-5">{t("admin.prod.col.views")}</th>
                        <th className="py-4 px-5 text-right">{t("admin.prod.col.actions")}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {products.length === 0 && (
                        <tr><td colSpan={8} className="text-center py-12 text-melilux-cream/50">{t("admin.prod.empty")}</td></tr>
                    )}
                    {products.map((p) => (
                        <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`product-row-${p.id}`}>
                            <td className="py-4 px-5">
                                <div className="flex items-center gap-4">
                                    {p.images?.[0] && (
                                        <img src={resolveImg(p.images[0])} loading="lazy" alt="" className="w-14 h-14 object-cover" />
                                    )}
                                    <div>
                                        <a href={`/collections/${p.category_slug}/${p.slug}`} target="_blank" rel="noopener noreferrer" className="font-serif text-lg hover:text-melilux-gold transition-colors">
                                            {p.name}
                                        </a>
                                        <div className="text-[0.65rem] uppercase tracking-[0.22em] text-melilux-cream/45 mt-1">{p.dimensions}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="py-4 px-5 text-melilux-cream/75">
                                {(p.category_names?.length ? p.category_names : [p.category_name].filter(Boolean)).join(", ") || "—"}
                            </td>
                            <td className="py-4 px-5 text-melilux-cream/65 text-xs">
                                {p.style ? (
                                    <span className="border border-white/15 px-2 py-1 uppercase tracking-[0.22em] text-[0.6rem]">{p.style}</span>
                                ) : <span className="text-melilux-cream/30">—</span>}
                            </td>
                            <td className="py-4 px-5 text-melilux-cream/85 text-sm">
                                <div className="flex flex-col gap-1">
                                    {(p.sale_price || p.sale_percentage) && (
                                        <span className="text-[0.6rem] uppercase tracking-[0.22em] px-2 py-0.5 border border-rose-500/40 text-rose-300 w-fit">
                                                -{p.sale_percentage}% off
                                            </span>
                                    )}
                                    {p.price || <span className="text-melilux-cream/30 text-xs italic">{t("admin.prod.onRequest")}</span>}
                                </div>
                            </td>
                            <td className="py-4 px-5">
                                    <span className={`text-[0.62rem] uppercase tracking-[0.22em] px-2 py-1 border ${
                                        p.status === "Available" ? "border-emerald-500/30 text-emerald-300" :
                                            p.status === "Limited" ? "border-melilux-gold/50 text-melilux-gold" :
                                                "border-white/15 text-melilux-cream/55"
                                    }`}>
                                        {p.status}
                                    </span>
                            </td>
                            <td className="py-4 px-5">
                                {p.featured ? <Star size={14} className="text-melilux-gold fill-melilux-gold" strokeWidth={1} /> : <span className="text-melilux-cream/30">—</span>}
                            </td>
                            <td className="py-4 px-5" data-testid={`product-views-${p.id}`}>
                                <span className="inline-flex items-center gap-1.5 text-melilux-cream/65 text-xs">
                                    <Eye size={13} strokeWidth={1.5} className="text-melilux-cream/40" />
                                    {p.views ?? 0}
                                </span>
                            </td>
                            <td className="py-4 px-5 text-right">
                                <div className="inline-flex gap-1 items-center">
                                    <a href={`/collections/${p.category_slug}/${p.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`view-product-${p.id}`} className="p-2 hover:text-melilux-gold transition-colors">
                                        <ExternalLink size={15} strokeWidth={1.5} />
                                    </a>
                                    <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="p-2 hover:text-melilux-gold transition-colors">
                                        <Pencil size={15} strokeWidth={1.5} />
                                    </button>
                                    <button
                                        onClick={() => remove(p)}
                                        data-testid={`delete-product-${p.id}`}
                                        title={confirmId === p.id ? t("admin.delete.again") : t("admin.delete.tip")}
                                        className={`p-2 transition-colors ${confirmId === p.id ? "text-destructive bg-destructive/15 ring-1 ring-destructive/50" : "hover:text-destructive"}`}
                                    >
                                        <Trash2 size={15} strokeWidth={1.5} />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            )}

            {view === "grid" && (
                <div data-testid="products-grid-admin">
                    {products.length === 0 ? (
                        <p className="text-center py-12 text-melilux-cream/50 border border-white/10">{t("admin.prod.empty")}</p>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
                            {products.map((p) => (
                                <div key={p.id} className="border border-white/10 bg-melilux-bg flex flex-col" data-testid={`product-card-admin-${p.id}`}>
                                    <div className="relative aspect-[4/5] bg-melilux-surface">
                                        {p.images?.[0] && (
                                            <img src={resolveImg(p.images[0], 800)} loading="lazy" alt="" className="absolute inset-0 w-full h-full object-contain" />
                                        )}
                                        {(p.sale_price || p.sale_percentage) && (
                                            <span className="absolute top-2 left-2 text-[0.6rem] uppercase tracking-[0.22em] px-2 py-1 bg-melilux-gold text-melilux-bg">
                                                −{p.sale_percentage}%
                                            </span>
                                        )}
                                        {p.featured && (
                                            <Star size={16} className="absolute top-2 right-2 text-melilux-gold fill-melilux-gold drop-shadow" strokeWidth={1} />
                                        )}
                                        {!p.visible && (
                                            <span className="absolute bottom-2 left-2 text-[0.6rem] uppercase tracking-[0.22em] px-2 py-1 bg-melilux-bg/80 border border-white/20 text-melilux-cream/70">
                                                Hidden
                                            </span>
                                        )}
                                    </div>
                                    <div className="p-4 flex flex-col gap-1.5 flex-1">
                                        <span className="font-serif text-lg leading-tight">{p.name}</span>
                                        <span className="text-[0.62rem] uppercase tracking-[0.22em] text-melilux-cream/45">
                                            {(p.category_names?.length ? p.category_names : [p.category_name].filter(Boolean)).join(", ") || "—"}
                                        </span>
                                        <span className="inline-flex items-center gap-1.5 text-[0.62rem] text-melilux-cream/45" data-testid={`product-views-grid-${p.id}`}>
                                            <Eye size={12} strokeWidth={1.5} />
                                            {p.views ?? 0}
                                        </span>
                                        <div className="flex items-center justify-between mt-auto pt-2">
                                            <span className="text-sm text-melilux-cream/85">
                                                {p.price || <span className="text-melilux-cream/30 text-xs italic">{t("admin.prod.onRequest")}</span>}
                                            </span>
                                            <div className="inline-flex items-center">
                                                <a href={`/collections/${p.category_slug}/${p.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`view-product-grid-${p.id}`} className="p-2 hover:text-melilux-gold transition-colors">
                                                    <ExternalLink size={15} strokeWidth={1.5} />
                                                </a>
                                                <button onClick={() => openEdit(p)} data-testid={`edit-product-grid-${p.id}`} className="p-2 hover:text-melilux-gold transition-colors">
                                                    <Pencil size={15} strokeWidth={1.5} />
                                                </button>
                                                <button
                                                    onClick={() => remove(p)}
                                                    data-testid={`delete-product-grid-${p.id}`}
                                                    title={confirmId === p.id ? t("admin.delete.again") : t("admin.delete.tip")}
                                                    className={`p-2 transition-colors ${confirmId === p.id ? "text-destructive bg-destructive/15 ring-1 ring-destructive/50" : "hover:text-destructive"}`}
                                                >
                                                    <Trash2 size={15} strokeWidth={1.5} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {total > 0 && (
                <div className="mt-5 flex items-center justify-between text-[0.7rem] uppercase tracking-[0.25em] text-melilux-cream/55" data-testid="products-pagination">
                    <div className="flex items-center gap-3">
                        <span>{t("admin.prod.perPage")}</span>
                        {[20, 50, 100].map((n) => (
                            <button
                                key={n}
                                onClick={() => { setPageSize(n); setPage(1); }}
                                className={`px-3 py-1.5 border transition-colors ${
                                    pageSize === n ? "border-melilux-gold text-melilux-gold" : "border-white/15 hover:border-melilux-cream/40"
                                }`}
                            >
                                {n}
                            </button>
                        ))}
                    </div>
                    <span>
                        Showing <span className="text-melilux-cream">{(page - 1) * pageSize + 1}</span>–
                        <span className="text-melilux-cream">{Math.min(page * pageSize, total)}</span> of{" "}
                        <span className="text-melilux-cream">{total}</span>
                    </span>
                    <div className="flex items-center gap-1">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="page-prev" className="p-2 border border-white/15 hover:border-melilux-gold hover:text-melilux-gold disabled:opacity-30 transition-colors">
                            <ChevronLeft size={14} strokeWidth={1.5} />
                        </button>
                        <span className="px-3" data-testid="page-indicator">
                            <span className="text-melilux-cream">{page}</span> / {totalPages}
                        </span>
                        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="page-next" className="p-2 border border-white/15 hover:border-melilux-gold hover:text-melilux-gold disabled:opacity-30 transition-colors">
                            <ChevronRight size={14} strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            )}

            {cropSrc && (
                <ImageCropDialog
                    src={cropSrc}
                    aspect={3 / 4}
                    title={t("admin.prod.field.card.cropTitle")}
                    onCancel={closeCardCrop}
                    onConfirm={uploadCardCrop}
                />
            )}

            {open && (
                <div
                    className="fixed inset-0 z-50 bg-melilux-bg/85 backdrop-blur-sm flex items-center justify-center p-6"
                    onClick={() => setOpen(false)}
                    data-testid="product-modal"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-3xl bg-melilux-bg border border-white/15 max-h-[92vh] flex flex-col"
                    >
                        <div className="overflow-y-auto min-h-0 p-8 md:p-10">
                        <p className="overline">{editing ? t("admin.prod.modal.edit.overline") : t("admin.prod.modal.new.overline")}</p>
                        <h2 className="font-serif text-3xl mt-3 mb-7">
                            {editing ? t("admin.prod.modal.edit.title") : t("admin.prod.modal.new.title")}
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <Field label={t("admin.prod.field.name")} className="md:col-span-2">
                                <input className="editorial-input" value={form.name} onChange={(e) => { const v = e.target.value; setForm({ ...form, name: v, slug: editing ? form.slug : slugify(v) }); }} data-testid="prod-name" />
                            </Field>

                            <Field label={t("admin.prod.field.description")} className="md:col-span-2">
                                <textarea className="editorial-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="prod-description" />
                            </Field>

                            <Field label={t("admin.prod.field.collections")}>
                                <div className="border border-white/15 max-h-48 overflow-y-auto divide-y divide-white/5" data-testid="prod-categories">
                                    {categories.length === 0 && (
                                        <p className="px-3 py-3 text-xs text-melilux-cream/40">{t("admin.prod.field.collection.placeholder")}</p>
                                    )}
                                    {categories.map((c) => (
                                        <label key={c.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-white/[0.03] transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={form.category_ids.includes(c.id)}
                                                onChange={() => toggleCategory(c.id)}
                                                className="w-4 h-4 accent-melilux-gold shrink-0"
                                                data-testid={`prod-category-${c.id}`}
                                            />
                                            <span className="text-sm text-melilux-cream/85">{c.name}</span>
                                            {form.category_ids[0] === c.id && (
                                                <span className="ml-auto text-[0.55rem] uppercase tracking-[0.22em] text-melilux-gold">{t("admin.prod.field.collections.primary")}</span>
                                            )}
                                        </label>
                                    ))}
                                </div>
                                <p className="text-[0.65rem] uppercase tracking-[0.22em] text-melilux-cream/40 mt-2">{t("admin.prod.field.collections.hint")}</p>
                            </Field>

                            <Field label={t("admin.prod.field.status")}>
                                <select className="bg-transparent border-b border-white/25 focus:border-melilux-gold outline-none w-full py-3.5 text-sm text-melilux-cream" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="prod-status">
                                    {STATUSES.map((s) => <option key={s} value={s} className="bg-melilux-bg">{s}</option>)}
                                </select>
                            </Field>

                            <Field label={t("admin.prod.field.style")}>
                                <select className="bg-transparent border-b border-white/25 focus:border-melilux-gold outline-none w-full py-3.5 text-sm text-melilux-cream" value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })} data-testid="prod-style">
                                    <option value="" className="bg-melilux-bg">{t("admin.prod.field.style.none")}</option>
                                    {STYLES.map((s) => <option key={s} value={s} className="bg-melilux-bg">{s}</option>)}
                                </select>
                            </Field>

                            <Field label={t("admin.prod.field.currency")}>
                                <select className="bg-transparent border-b border-white/25 focus:border-melilux-gold outline-none w-full py-3.5 text-sm text-melilux-cream" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="prod-currency">
                                    <option value="EUR" className="bg-melilux-bg">EUR — Euro (€)</option>
                                    <option value="MDL" className="bg-melilux-bg">MDL — Lei Moldovenești (L)</option>
                                </select>
                            </Field>

                            <Field label={t("admin.prod.field.price")}>
                                <input className="editorial-input" placeholder={form.currency === "MDL" ? 'e.g. "24,000 L"' : 'e.g. "€2,400"'} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="prod-price" />
                            </Field>

                            <Field label={t("admin.prod.field.salePrice")}>
                                <input className="editorial-input" placeholder='e.g. "€1,900"' value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="prod-sale-price" />
                            </Field>

                            <Field label={t("admin.prod.field.salePercent")}>
                                <input className="editorial-input" type="number" min="1" max="99" placeholder="e.g. 20" value={form.sale_percentage} onChange={(e) => setForm({ ...form, sale_percentage: e.target.value })} data-testid="prod-sale-percentage" />
                            </Field>

                            <Field label={t("admin.prod.field.room")}>
                                <input className="editorial-input" placeholder="e.g. Living Room" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} data-testid="prod-room" />
                            </Field>

                            <Field label={t("admin.prod.field.dimensions")}>
                                <input className="editorial-input" placeholder="W × D × H cm" value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} data-testid="prod-dimensions" />
                            </Field>

                            <Field label={t("admin.prod.field.materials")} className="md:col-span-2">
                                <div className="flex gap-2">
                                    <input
                                        className="editorial-input flex-1"
                                        placeholder={t("admin.prod.field.materials.placeholder")}
                                        value={materialInput}
                                        onChange={(e) => setMaterialInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMaterial(); } }}
                                        data-testid="prod-material-input"
                                    />
                                    <button type="button" onClick={addMaterial} className="text-[0.65rem] uppercase tracking-[0.25em] border border-white/15 px-3 hover:border-melilux-gold hover:text-melilux-gold transition-colors" data-testid="prod-material-add">
                                        {t("admin.prod.field.materials.add")}
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {form.materials.map((m) => (
                                        <span key={m} className="inline-flex items-center gap-2 border border-white/15 text-xs px-3 py-1.5 text-melilux-cream/80">
                                            {m}
                                            <button onClick={() => removeMaterial(m)} className="text-melilux-cream/50 hover:text-destructive"><X size={12} /></button>
                                        </span>
                                    ))}
                                </div>
                            </Field>

                            <Field label={t("admin.prod.field.care")} className="md:col-span-2">
                                <textarea className="editorial-input" rows={2} value={form.care_instructions} onChange={(e) => setForm({ ...form, care_instructions: e.target.value })} data-testid="prod-care" />
                            </Field>

                            <Field label={t("admin.prod.field.images")} className="md:col-span-2">
                                {form.images.length > 0 && (
                                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-3">
                                        {form.images.map((img, i) => (
                                            <div key={i} className="relative aspect-square group/img">
                                                <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                                                <button onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-melilux-bg/80 border border-white/15 p-1 hover:border-destructive hover:text-destructive" data-testid={`prod-img-remove-${i}`}>
                                                    <X size={12} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => openCardCrop(img)}
                                                    data-testid={`prod-img-crop-${i}`}
                                                    className="absolute bottom-0 inset-x-0 bg-melilux-bg/85 border-t border-white/10 py-1.5 text-[0.55rem] uppercase tracking-[0.2em] text-melilux-cream/70 opacity-0 group-hover/img:opacity-100 hover:text-melilux-gold transition-opacity"
                                                >
                                                    <Crop size={11} strokeWidth={1.5} className="inline mr-1 -mt-0.5" />
                                                    {t("admin.prod.field.card.use")}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <label className="inline-block cursor-pointer border border-white/15 px-4 py-2 text-[0.65rem] uppercase tracking-[0.25em] hover:border-melilux-gold hover:text-melilux-gold transition-colors">
                                    {uploading ? t("admin.prod.field.images.uploading") : t("admin.prod.field.images.upload")}
                                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }} data-testid="prod-image-upload" />
                                </label>
                                <p className="text-[0.65rem] uppercase tracking-[0.22em] text-melilux-cream/40 mt-2">{t("admin.prod.field.images.hint")}</p>
                            </Field>

                            <Field label={t("admin.prod.field.card")} className="md:col-span-2">
                                <p className="text-[0.65rem] uppercase tracking-[0.22em] text-melilux-cream/40 normal-case leading-relaxed">
                                    {t("admin.prod.field.card.hint")}
                                </p>
                                <div className="mt-3 flex items-end gap-4">
                                    <div className="w-28 aspect-[3/4] bg-melilux-surface border border-white/10 overflow-hidden shrink-0" data-testid="prod-card-image">
                                        {(form.card_image || form.images[0]) && (
                                            <img src={resolveImg(form.card_image || form.images[0], 320)} alt="" className="w-full h-full object-cover" />
                                        )}
                                    </div>
                                    <div className="text-[0.65rem] uppercase tracking-[0.22em]">
                                        {form.card_image ? (
                                            <>
                                                <p className="text-melilux-gold">{t("admin.prod.field.card.custom")}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => setForm({ ...form, card_image: "" })}
                                                    data-testid="prod-card-reset"
                                                    className="mt-2 text-melilux-cream/55 hover:text-destructive link-underline"
                                                >
                                                    {t("admin.prod.field.card.reset")}
                                                </button>
                                            </>
                                        ) : (
                                            <p className="text-melilux-cream/45">{t("admin.prod.field.card.auto")}</p>
                                        )}
                                    </div>
                                </div>
                            </Field>

                            <Field label={t("admin.prod.field.featured")} className="md:col-span-2">
                                <label className="inline-flex items-center gap-3 cursor-pointer">
                                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-melilux-gold" data-testid="prod-featured" />
                                    <span className="text-sm text-melilux-cream/75">{t("admin.prod.field.featured.label")}</span>
                                </label>
                            </Field>

                            <Field label={t("admin.prod.field.visible")} className="md:col-span-2">
                                <label className="inline-flex items-center gap-3 cursor-pointer">
                                    <input type="checkbox" checked={form.visible ?? true} onChange={(e) => setForm({ ...form, visible: e.target.checked })} className="w-4 h-4 accent-melilux-gold" data-testid="prod-visible" />
                                    <span className="text-sm text-melilux-cream/75">{t("admin.prod.field.visible.label")}</span>
                                </label>
                            </Field>
                        </div>
                        </div>

                        <div className="shrink-0 px-8 md:px-10 py-5 border-t border-white/10 flex justify-between gap-3">
                            <button onClick={() => setOpen(false)} className="px-6 py-3 text-[0.7rem] uppercase tracking-[0.28em] border border-white/15 hover:border-melilux-cream/40 transition-colors" data-testid="prod-cancel">
                                {t("admin.prod.cancel")}
                            </button>
                            <button onClick={save} disabled={saving} data-testid="prod-save" className="px-7 py-3 text-[0.7rem] uppercase tracking-[0.28em] bg-melilux-gold text-melilux-bg hover:bg-melilux-goldBright transition-colors disabled:opacity-60">
                                {saving ? t("admin.prod.saving") : editing ? t("admin.prod.save") : t("admin.prod.create")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <div className={className}>
            <label className="text-[0.65rem] uppercase tracking-[0.28em] text-melilux-cream/55">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}